import { createSelector, createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

export const UserRoles = {
  USER: 'user',
  PRACTITIONER: 'practitioner',
  PARTNER: 'partner',
  ADMIN: 'admin',
  IMPERSONATE: 'impersonate',
};
// Types
interface UserState {
  username?: string;
  isAuthenticated: boolean;
  impersonateId?: string;
  impersonateKitId?: string;
  hasDeclinedImpersonate?: boolean;
  email?: string;
  phoneNumber?: string;
  practitionerCode?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  role?: string;
  roles?: string[];
  status?: string;
  isAutoLoggedOut?: boolean;
  lastLoginAt?: string;
}

export type UserAttributes = {
  id?: string;
  status?: string;
  email?: string;
  role?: string;
  roles?: string[];
  organization?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  practitioner_code?: string;
  last_login_at?: string;
};

export type SignInPayload = Partial<UserAttributes>;

// State
export const initialState: UserState = {
  isAuthenticated: false,
  isAutoLoggedOut: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    signIn(state, action: PayloadAction<SignInPayload>) {
      const attributes = action.payload;

      state.isAuthenticated = true;

      state.role = attributes.role;
      state.roles = attributes.roles;
      state.status = attributes.status || 'active';
      state.email = attributes.email;
      state.phoneNumber = attributes.phone_number;
      state.firstName = attributes.first_name;
      state.lastName = attributes.last_name;
      state.username = attributes.id;
      state.practitionerCode = attributes.practitioner_code;
      state.isAutoLoggedOut = false;
      delete state.password;
      state.lastLoginAt = attributes.last_login_at;
    },
    signOut(state) {
      return {
        ...initialState,
        email: state.email,
        isAutoLoggedOut: state.isAutoLoggedOut,
      };
    },
    impersonate(state, action: PayloadAction<string>) {
      state.impersonateId = action.payload;
    },
    impersonateKit(state, action: PayloadAction<string | undefined>) {
      state.impersonateKitId = action.payload;
    },
    skipImpersonate(state) {
      state.hasDeclinedImpersonate = true;
      delete state.impersonateId;
    },
    storeEmail(state, action: PayloadAction<string>) {
      state.email = action.payload;
    },
    saveTempPassword(state, action: PayloadAction<string>) {
      state.password = action.payload;
    },
    setIsAutoLoggedOut(state) {
      state.isAutoLoggedOut = true;
    },
  },
});

// Selectors
export const getUser = (state: RootState) => state.user;
export const getIsAuthenticated = createSelector(
  getUser,
  user => user.isAuthenticated,
);

export const getRole = createSelector(getUser, user => user.role);
export const getRoles = createSelector(getUser, user => user.roles);
export const getEmail = createSelector(getUser, user => user.email);
export const getPhoneNumber = createSelector(getUser, user => user.phoneNumber);
export const getFirstName = createSelector(getUser, user => user.firstName);
export const getLastName = createSelector(getUser, user => user.lastName);
export const getUsername = createSelector(getUser, user => user.username);
export const getTempPassword = createSelector(getUser, user => user.password);
export const getPractitionerCode = createSelector(
  getUser,
  user => user.practitionerCode,
);

export const getIsActive = createSelector(
  getUser,
  user => user.status === 'active',
);

export const getImpersonateId = createSelector(
  getUser,
  user => user.impersonateId,
);

export const getImpersonateKitId = createSelector(
  getUser,
  user => user.impersonateKitId,
);

export const getIsAutoLoggedOut = createSelector(
  getUser,
  user => user.isAutoLoggedOut,
);
export const getLastLoginAt = createSelector(getUser, user => user.lastLoginAt);

export const getIsImpersonatorUserAdmin = createSelector(
  getUser,
  user => !!user.impersonateId && (user.roles ?? []).includes(UserRoles.ADMIN),
);

export const {
  signIn,
  signOut,
  impersonate,
  impersonateKit,
  setIsAutoLoggedOut,
  skipImpersonate,
  saveTempPassword,
  storeEmail,
} = userSlice.actions;
export default userSlice.reducer;
